import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthApiError, getAuthClient } from '@/api/client';
import { errorMessage } from '@/lib/errorMap';
import { useAuthStore } from '@/stores/useAuthStore';

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  if (!user) return null;

  return (
    <AppShell>
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <h1 className="text-2xl font-semibold">내 프로필</h1>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>기본 정보</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                수정
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPwOpen(true)}>
                비밀번호 변경
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
              <dt className="text-muted-foreground">이름</dt>
              <dd>{user.name}</dd>
              <dt className="text-muted-foreground">이메일</dt>
              <dd>{user.email ?? '—'}</dd>
              <dt className="text-muted-foreground">person_id</dt>
              <dd>{user.has_person_id ? '등록됨' : '없음'}</dd>
              <dt className="text-muted-foreground">전화</dt>
              <dd>{user.phone ?? '—'}</dd>
              <dt className="text-muted-foreground">상태</dt>
              <dd>{user.status}</dd>
              <dt className="text-muted-foreground">최근 로그인</dt>
              <dd>
                {user.last_login_at
                  ? new Date(user.last_login_at).toLocaleString('ko-KR')
                  : '—'}
              </dd>
            </dl>
          </CardContent>
        </Card>
      </div>

      <EditProfileDialog
        open={editing}
        onOpenChange={setEditing}
        initialName={user.name}
        initialPhone={user.phone ?? ''}
        onSaved={() => {
          void refreshMe();
        }}
      />

      <ChangePasswordDialog
        open={pwOpen}
        onOpenChange={setPwOpen}
        onChanged={async () => {
          toast.success('비밀번호가 변경됐습니다. 다시 로그인하세요.');
          await logout();
          navigate('/login', { replace: true });
        }}
      />
    </AppShell>
  );
}

function EditProfileDialog({
  open,
  onOpenChange,
  initialName,
  initialPhone,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  initialPhone: string;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await getAuthClient().updateMe({ name, phone: phone || undefined });
      toast.success('저장됐습니다');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof AuthApiError) toast.error(errorMessage(err.code));
      else toast.error('네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>프로필 수정</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ed-name">이름</Label>
            <Input
              id="ed-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ed-phone">전화번호</Label>
            <Input
              id="ed-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              취소
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? '저장 중...' : '저장'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({
  open,
  onOpenChange,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => Promise<void> | void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error('새 비밀번호 확인이 일치하지 않습니다');
      return;
    }
    setSubmitting(true);
    try {
      await getAuthClient().changePassword(current, next);
      onOpenChange(false);
      await onChanged();
    } catch (err) {
      if (err instanceof AuthApiError) toast.error(errorMessage(err.code));
      else toast.error('네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>비밀번호 변경</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            변경 후 모든 세션이 종료되어 다시 로그인이 필요합니다.
          </p>
          <div className="flex flex-col gap-2">
            <Label htmlFor="cur">현재 비밀번호</Label>
            <Input
              id="cur"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="nxt">새 비밀번호 (10자, 3종 이상)</Label>
            <Input
              id="nxt"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={10}
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="cnf">새 비밀번호 확인</Label>
            <Input
              id="cnf"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              취소
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? '변경 중...' : '변경'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
